import * as THREE from 'three'

import EventEmitter from '../Utils/EventEmitter.js'

import ScanModel from '../ScanModel.js'

import fadeVertexShader from '../shaders/fade.vertex.glsl'
import fadeFragmentShader from '../shaders/fade.fragment.glsl'

import tankVertexShader from '../shaders/tank.vertex.glsl'
import tankFragmentShader from '../shaders/tank.fragment.glsl'

export default class Tank extends EventEmitter
{
    constructor(_options = {})
    {
        super()

        // Set up
        this.$eventHub = _options.$eventHub
        this.mobile = _options.mobile
        this.camera = _options.camera
        this.sizes = _options.sizes
        this.time = _options.time
        this.cursor = _options.cursor
        this.ui = _options.ui
        this.loader = _options.loader

        // Container
        this.container = new THREE.Object3D()

        this.setRunning()
        this.setScene()
        this.setTank()
        this.setTablet()
    }

    /**
     * Set running
     */
    setRunning()
    {
        this.running = false

        this.$eventHub.$on('experience.tank.start', () =>
        {
            this.running = true
        })

        this.$eventHub.$on('experience.tank.stop', () =>
        {
            this.running = false
        })
    }

    /**
     * Set scene
     */
    setScene()
    {
        this.scene = {}

        /**
         * Floor
         */
        this.scene.floor = {}

        this.scene.floor.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uColor: { value: new THREE.Vector3(0.2, 0.2, 0.2) },

                uFocusPosition: { value: new THREE.Vector3(0, 0, 0) },
                uFocusDistance: { value: new THREE.Vector2(0, 10) }
            },
            vertexShader: fadeVertexShader,
            fragmentShader: fadeFragmentShader
        })

        this.scene.floor.geometry = this.loader.resources.barrelsSceneFloorModelObj.geometry

        this.scene.floor.mesh = new THREE.LineSegments(this.scene.floor.geometry, this.scene.floor.material)
        this.scene.floor.mesh.scale.x = 0.05
        this.scene.floor.mesh.scale.y = 0.05
        this.scene.floor.mesh.scale.z = 0.05
        this.container.add(this.scene.floor.mesh)

        /**
         * Delimiter
         */
        this.scene.delimiter = {}

        this.scene.delimiter.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            uniforms:
            {
                uColor: { value: new THREE.Vector3(0.7, 0.7, 0.7) },

                uFocusPosition: { value: new THREE.Vector3(0, 0, - 2) },
                uFocusDistance: { value: new THREE.Vector2(0, 8) }
            },
            vertexShader: fadeVertexShader,
            fragmentShader: fadeFragmentShader
        })

        this.scene.delimiter.geometry = this.loader.resources.barrelsSceneDelimiterModelObj.geometry

        this.scene.delimiter.mesh = new THREE.LineSegments(this.scene.delimiter.geometry, this.scene.delimiter.material)
        this.scene.delimiter.mesh.scale.x = 0.05
        this.scene.delimiter.mesh.scale.y = 0.05
        this.scene.delimiter.mesh.scale.z = 0.05
        this.scene.delimiter.mesh.position.y = 0.002
        this.container.add(this.scene.delimiter.mesh)

        /**
         * Room
         */
        this.scene.room = {}

        this.scene.room.material = new THREE.MeshBasicMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            // depthTest: false,
            blending: THREE.AdditiveBlending,
            color: 0x222222
        })

        this.scene.room.geometry = this.loader.resources.barrelsSceneRoomModelObj.geometry

        this.scene.room.mesh = new THREE.LineSegments(this.scene.room.geometry, this.scene.room.material)
        this.scene.room.mesh.scale.x = 0.05
        this.scene.room.mesh.scale.y = 0.05
        this.scene.room.mesh.scale.z = 0.05
        this.scene.room.mesh.position.y = 0.01
        this.container.add(this.scene.room.mesh)
    }

    /**
     * Set tank
     */
    setTank()
    {
        this.tank = {}

        // Container
        this.tank.container = new THREE.Object3D()
        this.tank.container.scale.x = 0.075
        this.tank.container.scale.y = 0.075
        this.tank.container.scale.z = 0.075
        this.tank.container.position.y = 0.11
        this.container.add(this.tank.container)

        // Object
        this.tank.object = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.tankModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 0.18, y: 0.18, z: 0.18 }
        })
        this.tank.container.add(this.tank.object.container)

        // Inside
        this.tank.inside = {}
        this.tank.inside.geometry = this.loader.resources.tankInsideModelObj.children[0].geometry
        this.tank.inside.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            depthTest: false,
            blending: THREE.AdditiveBlending,
            side: THREE.FrontSide,
            uniforms:
            {
                uPower: { type: 'f', value: 2 },
                uC: { type: 'f', value: 0.05 },
                uColor: { type: 'c', value: new THREE.Color(0x519bff) },
                uViewVector: { type: 'v3', value: new THREE.Vector3(0, 0, 1) },

                uPointA: { type: 'v2', value: new THREE.Vector2(0.25, 0.25) },
                uPointB: { type: 'v2', value: new THREE.Vector2(0.75, 0.25) },
                uPointC: { type: 'v2', value: new THREE.Vector2(0.75, 0.75) },
                uPointD: { type: 'v2', value: new THREE.Vector2(0.25, 0.75) },

                uProgress: { type: 'f', value: 0 }
            },
            vertexShader: tankVertexShader,
            fragmentShader: tankFragmentShader
        })

        this.tank.inside.mesh = new THREE.Mesh(this.tank.inside.geometry, this.tank.inside.material)

        this.tank.container.add(this.tank.inside.mesh)

        // Error
        this.tank.error = {}
        this.tank.error.geometry = this.loader.resources.tankErrorModelObj.children[0].geometry
        // this.tank.error.material = new THREE.MeshNormalMaterial()
        // this.tank.error.material = new THREE.MeshBasicMaterial({ color: 0xffe600, transparent: true, wireframe: false, opacity: 1 })
        this.tank.error.material = new THREE.ShaderMaterial({
            transparent: true,
            flatShading: true,
            // depthWrite: false,
            depthTest: false,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            uniforms:
            {
                uPower: { type: 'f', value: 2 },
                uC: { type: 'f', value: 0.5 },
                uColor: { type: 'c', value: new THREE.Color(0xff2900) },
                uViewVector: { type: 'v3', value: new THREE.Vector3(0, 1, 0.7) },

                uPointA: { type: 'v2', value: new THREE.Vector2(0.25, 0.25) },
                uPointB: { type: 'v2', value: new THREE.Vector2(0.75, 0.25) },
                uPointC: { type: 'v2', value: new THREE.Vector2(0.75, 0.75) },
                uPointD: { type: 'v2', value: new THREE.Vector2(0.25, 0.75) },

                uProgress: { type: 'f', value: 0 }
            },
            vertexShader: tankVertexShader,
            fragmentShader: tankFragmentShader
        })

        this.tank.error.mesh = new THREE.Mesh(this.tank.error.geometry, this.tank.error.material)

        this.tank.container.add(this.tank.error.mesh)

        // Progress update event
        this.$eventHub.$on('experience.tank.updateProgress', (_progress) =>
        {
            this.tank.error.material.uniforms.uProgress.value = _progress
            this.tank.inside.material.uniforms.uProgress.value = _progress
        })

        // Time tick event
        this.time.on('tick', () =>
        {
            // this.tank.error.material.uniforms.uC.value = 1 - Math.sin(this.time.elapsed * 0.004) * 0.5
            this.tank.error.material.uniforms.uPower.value = 1 + Math.sin(this.time.elapsed * 0.004) * 1
        })
    }

    /**
     * Set tablet
     */
    setTablet()
    {
        this.tablet = {}

        this.tablet.rotation = {}
        this.tablet.rotation.y = - Math.PI * 0.5
        this.tablet.rotation.z = Math.PI * 0.5

        this.tablet.position = {}
        this.tablet.position.x = 0
        this.tablet.position.y = 0.132
        this.tablet.position.z = - 0.6

        // Container
        this.tablet.container = new THREE.Object3D()
        this.tablet.container.scale.x = 0.06
        this.tablet.container.scale.y = 0.06
        this.tablet.container.scale.z = 0.06
        this.tablet.container.position.x = this.tablet.position.x
        this.tablet.container.position.y = this.tablet.position.y
        this.tablet.container.position.z = this.tablet.position.z
        this.tablet.container.rotation.y = this.tablet.rotation.y
        this.tablet.container.rotation.z = this.tablet.rotation.z
        this.container.add(this.tablet.container)

        if(this.camera.type === 'guided')
        {
            this.time.on('tick', () =>
            {
                // Not running
                if(!this.running)
                {
                    return
                }

                const panX = this.camera.pan.value.x / this.camera.pan.amplitude.x
                const panY = this.camera.pan.value.y / this.camera.pan.amplitude.y

                this.tablet.container.rotation.y = this.tablet.rotation.y - panX
                this.tablet.container.rotation.z = this.tablet.rotation.z + panY
            })
        }

        // Points
        this.tablet.points = {}
        this.tablet.points.objects = {}
        this.tablet.points.objects.a = new THREE.Object3D()
        this.tablet.points.objects.a.position.x = - 0.75
        this.tablet.points.objects.a.position.z = - 1
        this.tablet.container.add(this.tablet.points.objects.a)
        this.tablet.points.objects.b = new THREE.Object3D()
        this.tablet.points.objects.b.position.x = - 0.75
        this.tablet.points.objects.b.position.z = 1
        this.tablet.container.add(this.tablet.points.objects.b)
        this.tablet.points.objects.c = new THREE.Object3D()
        this.tablet.points.objects.c.position.x = 0.75
        this.tablet.points.objects.c.position.z = 1
        this.tablet.container.add(this.tablet.points.objects.c)
        this.tablet.points.objects.d = new THREE.Object3D()
        this.tablet.points.objects.d.position.x = 0.75
        this.tablet.points.objects.d.position.z = - 1
        this.tablet.container.add(this.tablet.points.objects.d)

        // this.tablet.points.objects.a.add(new THREE.Mesh(new THREE.BoxBufferGeometry(0.02, 0.02, 0.02), new THREE.MeshBasicMaterial({ color: 0xff0000 })))
        // this.tablet.points.objects.b.add(new THREE.Mesh(new THREE.BoxBufferGeometry(0.02, 0.02, 0.02), new THREE.MeshBasicMaterial({ color: 0x00ff00 })))
        // this.tablet.points.objects.c.add(new THREE.Mesh(new THREE.BoxBufferGeometry(0.02, 0.02, 0.02), new THREE.MeshBasicMaterial({ color: 0x0000ff })))
        // this.tablet.points.objects.d.add(new THREE.Mesh(new THREE.BoxBufferGeometry(0.02, 0.02, 0.02), new THREE.MeshBasicMaterial({ color: 0xff00ff })))

        this.tablet.points.vectors = {}
        this.tablet.points.vectors.a = new THREE.Vector3()
        this.tablet.points.vectors.b = new THREE.Vector3()
        this.tablet.points.vectors.c = new THREE.Vector3()
        this.tablet.points.vectors.d = new THREE.Vector3()

        this.time.on('tick', () =>
        {
            // Not running
            if(!this.running)
            {
                return
            }

            this.tablet.points.objects.a.updateMatrixWorld()
            this.tablet.points.objects.b.updateMatrixWorld()
            this.tablet.points.objects.c.updateMatrixWorld()
            this.tablet.points.objects.d.updateMatrixWorld()

            this.tablet.points.vectors.a.setFromMatrixPosition(this.tablet.points.objects.a.matrixWorld)
            this.tablet.points.vectors.a.project(this.camera.instance)
            this.tablet.points.vectors.a.x = this.tablet.points.vectors.a.x
            this.tablet.points.vectors.a.y = this.tablet.points.vectors.a.y

            this.tablet.points.vectors.b.setFromMatrixPosition(this.tablet.points.objects.b.matrixWorld)
            this.tablet.points.vectors.b.project(this.camera.instance)
            this.tablet.points.vectors.b.x = this.tablet.points.vectors.b.x
            this.tablet.points.vectors.b.y = this.tablet.points.vectors.b.y

            this.tablet.points.vectors.c.setFromMatrixPosition(this.tablet.points.objects.c.matrixWorld)
            this.tablet.points.vectors.c.project(this.camera.instance)
            this.tablet.points.vectors.c.x = this.tablet.points.vectors.c.x
            this.tablet.points.vectors.c.y = this.tablet.points.vectors.c.y

            this.tablet.points.vectors.d.setFromMatrixPosition(this.tablet.points.objects.d.matrixWorld)
            this.tablet.points.vectors.d.project(this.camera.instance)
            this.tablet.points.vectors.d.x = this.tablet.points.vectors.d.x
            this.tablet.points.vectors.d.y = this.tablet.points.vectors.d.y

            this.tank.inside.material.uniforms.uPointA.value.x = this.tablet.points.vectors.a.x
            this.tank.inside.material.uniforms.uPointA.value.y = this.tablet.points.vectors.a.y

            this.tank.inside.material.uniforms.uPointB.value.x = this.tablet.points.vectors.b.x
            this.tank.inside.material.uniforms.uPointB.value.y = this.tablet.points.vectors.b.y

            this.tank.inside.material.uniforms.uPointC.value.y = this.tablet.points.vectors.c.y
            this.tank.inside.material.uniforms.uPointC.value.x = this.tablet.points.vectors.c.x

            this.tank.inside.material.uniforms.uPointD.value.x = this.tablet.points.vectors.d.x
            this.tank.inside.material.uniforms.uPointD.value.y = this.tablet.points.vectors.d.y

            this.tank.error.material.uniforms.uPointA.value.x = this.tablet.points.vectors.a.x
            this.tank.error.material.uniforms.uPointA.value.y = this.tablet.points.vectors.a.y

            this.tank.error.material.uniforms.uPointB.value.x = this.tablet.points.vectors.b.x
            this.tank.error.material.uniforms.uPointB.value.y = this.tablet.points.vectors.b.y

            this.tank.error.material.uniforms.uPointC.value.y = this.tablet.points.vectors.c.y
            this.tank.error.material.uniforms.uPointC.value.x = this.tablet.points.vectors.c.x

            this.tank.error.material.uniforms.uPointD.value.x = this.tablet.points.vectors.d.x
            this.tank.error.material.uniforms.uPointD.value.y = this.tablet.points.vectors.d.y
        })

        // Object
        this.tablet.object = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.tabletModelObj.geometry,
            revealPosition: { x: 0, y: 0.25, z: 0.68 },
            color: { x: 0.3, y: 0.3, z: 0.3 }
        })
        this.tablet.container.add(this.tablet.object.container)
    }

    /**
     * Reset
     */
    reset()
    {
        this.tank.error.material.uniforms.uProgress.value = 0
        this.tank.inside.material.uniforms.uProgress.value = 0
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Interactions/Tank.js